import type { LevelDraft, PromptDraft } from "@/components/admin/canonical-builder/types";
import { createClientId } from "@/components/admin/canonical-builder/clientId";
import {
    hydrationContentSelectionPriority,
    type HydrationGeneratedLessonRecord,
} from "@/components/admin/canonical-builder/hydrationContentPriority";

export type CanonicalApiResponse = {
    id: number;
    title: string;
    description: string | null;
    generation_prompt: string | null;
    proactive_prompt: string | null;
    quiz_prompt: string | null;
    placement_quiz_prompt: string | null;
    remedial_lesson_prompt: string | null;
    course_organizations?: Array<{ organization_id: number }>;
    levels: Array<{ id: number; name: string; level_number: number }>;
    modules: Array<{
        id: number;
        title: string;
        levels: { id: number } | null;
        lessons: Array<{
            id: number;
            title: string;
            generated_lessons: unknown;
        }>;
    }>;
};

export type MetadataApiResponse = {
    domains: Array<{ label: string }>;
    goals: Array<{ label: string }>;
};

export type CanonicalDraftHydrationData = {
    title: string;
    description: string;
    prompts: PromptDraft;
    domains: string[];
    futureGoals: string[];
    organizationId: string;
    levels: LevelDraft[];
};

const stringifyContent = (value: unknown) => {
    if (value === null || value === undefined) return "";
    try {
        if (typeof value === "string") {
            const parsed = JSON.parse(value);
            return JSON.stringify(parsed, null, 2);
        }
        return JSON.stringify(value, null, 2);
    } catch {
        return typeof value === "string" ? value : "";
    }
};

const normalizeGeneratedLessons = (generatedLessons: unknown) => {
    if (Array.isArray(generatedLessons)) return generatedLessons;
    if (!generatedLessons || typeof generatedLessons !== "object") return [];

    const record = generatedLessons as Record<string, unknown>;
    const nested = record.generated_lessons;
    if (Array.isArray(nested)) return nested;
    if (nested && typeof nested === "object") return [nested];
    return [generatedLessons];
};

const getHydratedLessonContent = (generatedLessons: unknown) => {
    const normalizedLessons = normalizeGeneratedLessons(generatedLessons).filter(
        (item): item is HydrationGeneratedLessonRecord => !!item && typeof item === "object",
    );
    if (normalizedLessons.length === 0) return "";

    const preferred =
        hydrationContentSelectionPriority
            .map((rule) => normalizedLessons.find(rule))
            .find(Boolean) ?? normalizedLessons[0];

    if (preferred.structured_content != null) {
        const pretty = stringifyContent(preferred.structured_content);
        if (pretty.trim().length > 0) return pretty;
    }
    return typeof preferred.generated_text === "string" ? preferred.generated_text : "";
};

export const mapCanonicalDraftHydrationData = (
    course: CanonicalApiResponse,
    metadata: MetadataApiResponse,
): CanonicalDraftHydrationData => {
    const modulesByLevelId = new Map<number, CanonicalApiResponse["modules"]>();
    const modulesWithoutLevel: CanonicalApiResponse["modules"] = [];
    for (const moduleItem of course.modules ?? []) {
        if (!moduleItem.levels?.id) {
            modulesWithoutLevel.push(moduleItem);
            continue;
        }
        const group = modulesByLevelId.get(moduleItem.levels.id) ?? [];
        group.push(moduleItem);
        modulesByLevelId.set(moduleItem.levels.id, group);
    }

    const baseLevels = (course.levels ?? []).length > 0
        ? [...course.levels]
        : Array.from(
              new Map(
                  (course.modules ?? [])
                      .filter((moduleItem) => Boolean(moduleItem.levels?.id))
                      .map((moduleItem, index) => [
                          moduleItem.levels!.id,
                          {
                              id: moduleItem.levels!.id,
                              name: (moduleItem.levels as { name?: string } | null)?.name ?? `Level ${index + 1}`,
                              level_number: index + 1,
                          },
                      ]),
              ).values(),
          );

    const levels: LevelDraft[] = baseLevels
        .sort((a, b) => (a.level_number ?? 0) - (b.level_number ?? 0))
        .map((level) => ({
            id: createClientId(),
            name: level.name ?? "",
            sourceLevelId: level.id,
            modules: (modulesByLevelId.get(level.id) ?? []).map((module) => ({
                id: createClientId(),
                title: module.title ?? "",
                sourceModuleId: module.id,
                lessons: (module.lessons ?? []).map((lesson) => ({
                    id: createClientId(),
                    title: lesson.title ?? "",
                    content: getHydratedLessonContent(lesson.generated_lessons),
                    sourceLessonId: lesson.id,
                })),
            })),
        }));

    if (modulesWithoutLevel.length > 0) {
        levels.push({
            id: createClientId(),
            name: "General",
            sourceLevelId: undefined,
            modules: modulesWithoutLevel.map((module) => ({
                id: createClientId(),
                title: module.title ?? "",
                sourceModuleId: module.id,
                lessons: (module.lessons ?? []).map((lesson) => ({
                    id: createClientId(),
                    title: lesson.title ?? "",
                    content: getHydratedLessonContent(lesson.generated_lessons),
                    sourceLessonId: lesson.id,
                })),
            })),
        });
    }

    return {
        title: course.title ?? "",
        description: course.description ?? "",
        prompts: {
            generation_prompt: course.generation_prompt ?? "",
            proactive_prompt: course.proactive_prompt ?? "",
            quiz_prompt: course.quiz_prompt ?? "",
            placement_quiz_prompt: course.placement_quiz_prompt ?? "",
            remedial_lesson_prompt: course.remedial_lesson_prompt ?? "",
        },
        domains: metadata.domains.map((d) => d.label).filter(Boolean),
        futureGoals: metadata.goals.map((g) => g.label).filter(Boolean),
        organizationId: course.course_organizations?.[0]?.organization_id
            ? String(course.course_organizations[0].organization_id)
            : "",
        levels,
    };
};
